import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import RefreshIcon from "@material-ui/icons/Refresh";
import CropOriginalIcon from "@material-ui/icons/CropOriginal";
import CropSquareIcon from "@material-ui/icons/CropSquare";
import Crop169Icon from "@material-ui/icons/Crop169";
import CropPortraitIcon from "@material-ui/icons/CropPortrait";

// A moldura de pre-visualizacao cabe dentro desta caixa. Retrato usa o limite
// de altura, paisagem/quadrado o de largura — assim nada estoura o dialog.
const MOLDURA_MAX_LARG = 340;
const MOLDURA_MAX_ALT = 420;

// "Imagem inteira" e o padrao: a moldura assume a proporcao da PROPRIA imagem,
// entao ela aparece completa, sem corte e sem borda branca. Os outros formatos
// continuam disponiveis para quem quiser um enquadramento fixo — e mesmo neles
// a imagem agora CABE inteira (com borda), em vez de ser cortada. Quem quiser
// cortar de proposito e so aproximar com o zoom.
const FORMATOS = [
  { id: "original", rotulo: "Imagem inteira", proporcao: null, icone: CropOriginalIcon },
  { id: "quadrado", rotulo: "Quadrado", proporcao: 1, icone: CropSquareIcon },
  { id: "paisagem", rotulo: "Paisagem", proporcao: 4 / 3, icone: Crop169Icon },
  { id: "retrato", rotulo: "Retrato", proporcao: 3 / 4, icone: CropPortraitIcon },
];

const LADO_MAIOR_SAIDA = 1080;

const PASSO_ZOOM = 0.25;

const useStyles = makeStyles((theme) => ({
  conteudo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    paddingTop: 8,
  },
  palco: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "#131917" : "#eceff1",
    border: `1px solid ${theme.palette.divider}`,
    cursor: "grab",
    touchAction: "none",
    userSelect: "none",
    "&:active": { cursor: "grabbing" },
  },
  imagem: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transformOrigin: "center center",
    pointerEvents: "none",
    maxWidth: "none",
  },
  linhasGuia: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "linear-gradient(to right, rgba(255,255,255,0.35) 1px, transparent 1px)," +
      "linear-gradient(to bottom, rgba(255,255,255,0.35) 1px, transparent 1px)",
    backgroundSize: "33.33% 33.33%",
    opacity: 0.5,
  },
  linhaControle: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    maxWidth: MOLDURA_MAX_LARG,
  },
  formatos: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  botaoFormato: {
    textTransform: "none",
    borderRadius: 10,
    minWidth: 0,
    padding: "4px 12px",
    fontSize: "0.76rem",
  },
  dica: {
    fontSize: "0.74rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  salvar: {
    backgroundColor: "#1E9855",
    color: "#fff",
    textTransform: "none",
    fontWeight: 700,
    borderRadius: 10,
    "&:hover": { backgroundColor: "#17803f" },
  },
}));

const EditorDeImagem = ({ arquivo, open, onCancel, onConfirmar }) => {
  const classes = useStyles();

  const [origem, setOrigem] = useState("");
  const [imgNatural, setImgNatural] = useState(null);
  const [formato, setFormato] = useState("original");
  const [zoom, setZoom] = useState(1);
  const [rotacao, setRotacao] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [salvando, setSalvando] = useState(false);

  const arrasto = useRef(null);
  const palcoRef = useRef(null);

  // Proporcao (largura/altura) da moldura. No "Imagem inteira" ela segue a
  // propria imagem; nos formatos fixos, o valor da tabela.
  const proporcao = useMemo(() => {
    if (formato === "original") {
      return imgNatural ? imgNatural.largura / imgNatural.altura : 1;
    }
    return FORMATOS.find((f) => f.id === formato)?.proporcao || 1;
  }, [formato, imgNatural]);

  // Tamanho da moldura na tela, encaixado na caixa maxima (mantendo a proporcao).
  const { molduraLargura, molduraAltura } = useMemo(() => {
    let ml = MOLDURA_MAX_LARG;
    let ma = Math.round(MOLDURA_MAX_LARG / proporcao);
    if (ma > MOLDURA_MAX_ALT) {
      ma = MOLDURA_MAX_ALT;
      ml = Math.round(MOLDURA_MAX_ALT * proporcao);
    }
    return { molduraLargura: ml, molduraAltura: ma };
  }, [proporcao]);

  useEffect(() => {
    if (!arquivo) return undefined;

    const url = URL.createObjectURL(arquivo);
    setOrigem(url);

    const img = new Image();
    img.onload = () => {
      setImgNatural({ largura: img.naturalWidth, altura: img.naturalHeight });
    };
    img.src = url;

    return () => URL.revokeObjectURL(url);
  }, [arquivo]);

  const reiniciar = useCallback(() => {
    setRotacao(0);
    setPos({ x: 0, y: 0 });
    // O zoom volta ao "cabe inteira" pelo efeito de reenquadre abaixo.
  }, []);

  useEffect(() => {
    reiniciar();
  }, [arquivo, formato, reiniciar]);

  // "Caber" (contain): a imagem inteira entra na moldura. Antes era Math.max
  // (cover), que preenchia a moldura e CORTAVA as sobras.
  const escalaBase = imgNatural
    ? Math.min(
        molduraLargura / imgNatural.largura,
        molduraAltura / imgNatural.altura
      )
    : 1;

  const larguraExibida = imgNatural ? imgNatural.largura * escalaBase : 0;
  const alturaExibida = imgNatural ? imgNatural.altura * escalaBase : 0;

  // Zoom minimo = "imagem inteira cabendo". Sem rotacao, a escala base ja faz a
  // imagem caber, entao o minimo e 1. Girada 90/270, calculamos quanto ela
  // precisa encolher para caber com os lados trocados.
  const zoomMinimo = useMemo(() => {
    if (!imgNatural) return 1;
    if (rotacao % 180 === 0) return 1;
    if (!larguraExibida || !alturaExibida) return 1;
    return Math.min(
      molduraLargura / alturaExibida,
      molduraAltura / larguraExibida
    );
  }, [imgNatural, rotacao, alturaExibida, larguraExibida, molduraLargura, molduraAltura]);

  const zoomMaximo = Math.max(zoomMinimo, 1) * 4;

  const limitarPos = useCallback(
    (p, z, rot) => {
      if (!imgNatural) return p;

      const girado = rot % 180 !== 0;
      const larg = (girado ? alturaExibida : larguraExibida) * z;
      const alt = (girado ? larguraExibida : alturaExibida) * z;

      const maxX = Math.max(0, (larg - molduraLargura) / 2);
      const maxY = Math.max(0, (alt - molduraAltura) / 2);

      return {
        x: Math.min(maxX, Math.max(-maxX, p.x)),
        y: Math.min(maxY, Math.max(-maxY, p.y)),
      };
    },
    [imgNatural, larguraExibida, alturaExibida, molduraLargura, molduraAltura]
  );

  // Sempre que a moldura, a imagem ou a rotacao mudam, reenquadra para a imagem
  // inteira (zoom no minimo, centralizada). E o que garante "sem corte" por
  // padrao — o usuario so corta se aproximar com o zoom de proposito.
  useEffect(() => {
    setZoom(zoomMinimo);
    setPos({ x: 0, y: 0 });
  }, [zoomMinimo]);

  const ajustarZoom = (delta) => {
    setZoom((z) => {
      const alvo = Math.min(zoomMaximo, Math.max(zoomMinimo, z + delta));
      setPos((p) => limitarPos(p, alvo, rotacao));
      return alvo;
    });
  };

  const iniciarArrasto = (e) => {
    const ponto = e.touches ? e.touches[0] : e;
    arrasto.current = {
      x: ponto.clientX - pos.x,
      y: ponto.clientY - pos.y,
    };
  };

  const moverArrasto = (e) => {
    if (!arrasto.current) return;
    const ponto = e.touches ? e.touches[0] : e;

    setPos(
      limitarPos(
        {
          x: ponto.clientX - arrasto.current.x,
          y: ponto.clientY - arrasto.current.y,
        },
        zoom,
        rotacao
      )
    );
  };

  const soltarArrasto = () => {
    arrasto.current = null;
  };

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("mouseup", soltarArrasto);
    window.addEventListener("touchend", soltarArrasto);
    return () => {
      window.removeEventListener("mouseup", soltarArrasto);
      window.removeEventListener("touchend", soltarArrasto);
    };
  }, [open]);

  const aplicar = async () => {
    if (!imgNatural) return;
    setSalvando(true);

    try {
      const saidaLargura =
        proporcao >= 1 ? LADO_MAIOR_SAIDA : Math.round(LADO_MAIOR_SAIDA * proporcao);
      const saidaAltura =
        proporcao >= 1 ? Math.round(LADO_MAIOR_SAIDA / proporcao) : LADO_MAIOR_SAIDA;

      const fator = saidaLargura / molduraLargura;

      const canvas = document.createElement("canvas");
      canvas.width = saidaLargura;
      canvas.height = saidaAltura;

      const ctx = canvas.getContext("2d");

      // Fundo branco: se sobrar borda (imagem mais estreita/baixa que a
      // moldura), ela fica branca em vez de preta/transparente.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, saidaLargura, saidaAltura);

      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = origem;
      });

      ctx.save();
      ctx.translate(
        saidaLargura / 2 + pos.x * fator,
        saidaAltura / 2 + pos.y * fator
      );
      ctx.rotate((rotacao * Math.PI) / 180);
      ctx.scale(zoom, zoom);

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        img,
        (-larguraExibida * fator) / 2,
        (-alturaExibida * fator) / 2,
        larguraExibida * fator,
        alturaExibida * fator
      );
      ctx.restore();

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92)
      );

      if (blob) {
        const nome = `saudacao-${Date.now()}.jpg`;
        onConfirmar(new File([blob], nome, { type: "image/jpeg" }));
      }
    } catch (e) {
      console.error("Falha ao recortar a imagem:", e);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Ajustar imagem</DialogTitle>

      <DialogContent>
        <div className={classes.conteudo}>
          {}
          <div className={classes.formatos}>
            {FORMATOS.map((f) => {
              const Icone = f.icone;
              const ativo = formato === f.id;
              return (
                <Button
                  key={f.id}
                  size="small"
                  variant={ativo ? "contained" : "outlined"}
                  color={ativo ? "primary" : "default"}
                  className={classes.botaoFormato}
                  startIcon={<Icone fontSize="small" />}
                  onClick={() => setFormato(f.id)}
                >
                  {f.rotulo}
                </Button>
              );
            })}
          </div>

          {}
          <div
            ref={palcoRef}
            className={classes.palco}
            style={{ width: molduraLargura, height: molduraAltura }}
            onMouseDown={iniciarArrasto}
            onMouseMove={moverArrasto}
            onTouchStart={iniciarArrasto}
            onTouchMove={moverArrasto}
          >
            {origem && imgNatural && (
              <img
                className={classes.imagem}
                src={origem}
                alt="Ajuste"
                style={{
                  width: larguraExibida,
                  height: alturaExibida,
                  transform:
                    `translate(-50%, -50%) ` +
                    `translate(${pos.x}px, ${pos.y}px) ` +
                    `rotate(${rotacao}deg) scale(${zoom})`,
                }}
                draggable={false}
              />
            )}
            <div className={classes.linhasGuia} />
          </div>

          {}
          <div className={classes.linhaControle}>
            <Tooltip title="Afastar">
              <span>
                <IconButton
                  size="small"
                  onClick={() => ajustarZoom(-PASSO_ZOOM)}
                  disabled={zoom <= zoomMinimo + 0.001}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Slider
              value={zoom}
              min={zoomMinimo}
              max={zoomMaximo}
              step={0.01}
              onChange={(e, v) => {
                setZoom(v);
                setPos((p) => limitarPos(p, v, rotacao));
              }}
              aria-label="Zoom"
            />

            <Tooltip title="Aproximar">
              <span>
                <IconButton
                  size="small"
                  onClick={() => ajustarZoom(PASSO_ZOOM)}
                  disabled={zoom >= zoomMaximo - 0.001}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Girar 90°">
              <IconButton
                size="small"
                onClick={() => setRotacao((r) => (r + 90) % 360)}
              >
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Restaurar">
              <IconButton size="small" onClick={reiniciar}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>

          <Typography className={classes.dica}>
            A imagem aparece inteira, sem corte. Use o zoom se quiser aproximar,
            ou troque o formato acima.
          </Typography>
        </div>
      </DialogContent>

      <DialogActions style={{ padding: "8px 24px 20px" }}>
        <Button onClick={onCancel} style={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          className={classes.salvar}
          onClick={aplicar}
          disabled={!imgNatural || salvando}
        >
          {salvando ? "Aplicando..." : "Usar esta imagem"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditorDeImagem;
